export const CONTACT = {
  name: "My Geeks LLC",
  email: "hello@my-geeks.com",
  phone: "(888) 973-3308",
  phoneHref: "tel:8889733308",
  address: "1095 Sugarview Dr, Ste 100, Sheridan, WY 82801",
  country: "United States",
};

// Items marked `placeholder: true` have not been supplied yet and are rendered
// with a visible marker. They must be replaced before the site goes live.
export const BUSINESS = {
  legalName: { value: "My Geeks LLC", placeholder: false },
  businessType: { value: "Limited Liability Company (LLC)", placeholder: false },
  domain: { value: "my-geeks.com", placeholder: false },
  currency: { value: "USD", placeholder: false },
  taxId: { value: "[TAX / REGISTRATION ID, IF APPLICABLE]", placeholder: true },
  hours: { value: "Monday – Friday, 09:00 – 18:00 CST", placeholder: false },
  responseTime: { value: "within 5 hours", placeholder: false },
  established: { value: "2026", placeholder: false },
  ein: { value: "36-5186282", placeholder: false },
  registration: "Registered in the State of Wyoming, USA",
};

export const SITE_URL = process.env.REACT_APP_SITE_URL || "";

export const HERO_IMAGE =
  "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
export const TYPEWRITER_IMAGE =
  "https://images.unsplash.com/photo-1530819568329-97653eafbbfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwdHlwZXdyaXRlciUyMGlua3xlbnwwfHx8fDE3ODcxNjg3NTV8MA&ixlib=rb-4.1.0&q=85";
export const OFFICE_IMAGE =
  "https://images.pexels.com/photos/8872711/pexels-photo-8872711.jpeg";

export const SERVICES = [
  {
    slug: "website-development",
    path: "/website-development",
    numeral: "I",
    title: "Website Development",
    short: "Business website design and development, landing pages, redesigns, maintenance, performance work and basic SEO setup.",
    description:
      "Design and development of business websites — from single landing pages to multi-page corporate sites — including redesigns of existing sites, ongoing maintenance, performance improvements, basic on-page SEO setup, and contact or lead-capture forms.",
    included: [
      "Business website design",
      "Responsive website development (mobile, tablet, desktop)",
      "Landing page development",
      "Corporate / multi-page business websites",
      "Website redesign",
      "Website maintenance",
      "Website performance improvements",
      "Basic SEO setup (titles, descriptions, structure, sitemap)",
      "Contact forms and lead-generation functionality",
    ],
    notIncluded: [
      "Domain registration and hosting fees (billed by the provider, unless agreed otherwise)",
      "Paid stock photography, fonts or third-party licences",
      "Copywriting beyond agreed scope",
      "Guaranteed search rankings",
    ],
    timeframe: { value: "2–6 days, depending on the number of pages required", placeholder: false },
    pricing: "Request a Quote",
    pricingNote:
      "Final price depends on the number of pages, design complexity, integrations and content readiness. A written quote is issued before any work begins.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
    alt: "Laptop showing a business website and analytics",
  },
  {
    slug: "software-development",
    path: "/software-development",
    numeral: "II",
    title: "Software Development",
    short: "Custom business applications, client portals, dashboards, integrations and ongoing maintenance.",
    description:
      "Design and development of custom software for businesses — internal applications, client portals, reporting dashboards, system integrations and APIs — built to your written requirements, delivered in stages, and maintained under a monthly retainer if required.",
    included: [
      "Custom business applications (web-based)",
      "Client and customer portals",
      "Internal dashboards and reporting tools",
      "System integration and workflow automation",
      "API design and development",
      "Staging environment, testing and documentation",
      "Ongoing maintenance and support (monthly retainer)",
    ],
    notIncluded: [
      "Hosting, cloud infrastructure and third-party service fees (billed by the provider)",
      "Commercial software licences, if required",
      "Data entry or migration of historical records (quoted separately)",
      "Servicing of computers or devices",
    ],
    timeframe: { value: "Delivered in stages; first working version typically within 4–8 weeks", placeholder: false },
    pricing: "Request a Quote",
    pricingNote:
      "Cost depends on the number of features, complexity of business rules, integrations and design work. Billed by milestone as stated in the written quotation.",
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
    alt: "Software source code on a developer's screen",
  },
  {
    slug: "business-management",
    path: "/business-management",
    numeral: "III",
    title: "Business Management",
    short: "Process consulting, operations support, workflow planning, documentation, digital setup and website/content coordination.",
    description:
      "Consulting and hands-on support that helps a business run in an orderly way — reviewing how work gets done, planning workflows, organising documentation, assisting with digital tools and setup, and coordinating website and content tasks on your behalf.",
    included: [
      "Business process consulting",
      "Business operations support",
      "Business workflow planning",
      "Business documentation and organisation",
      "Digital business setup assistance (tools, accounts, basic systems)",
      "Business website / content coordination",
    ],
    notIncluded: [
      "Legal, tax or accounting advice (we are not a law or accountancy firm)",
      "Payroll processing or statutory filings",
      "Software licences or subscriptions for third-party tools",
    ],
    timeframe: { value: "Defined project of 3–4 weeks, or monthly retainer", placeholder: false },
    pricing: "Request a Quote",
    pricingNote:
      "Engagements are scoped individually because every business operates differently. We agree the scope, deliverables and fee in writing before starting.",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
    alt: "Business team reviewing operations around a table",
  },
  {
    slug: "seo-smo",
    path: "/seo-smo",
    numeral: "IV",
    title: "SEO / SMO Services",
    short: "Search engine optimisation and social media optimisation for business websites.",
    description:
      "On-page and technical search engine optimisation for business websites, together with social media optimisation — setting up and tidying business profiles so the website and social presence work together consistently.",
    included: [
      "Technical and on-page SEO review of the website",
      "Keyword research and page-level recommendations",
      "Title, meta description, heading and internal-link improvements",
      "Sitemap and indexing setup",
      "Social media profile setup and optimisation (business profiles)",
      "Monthly plain-English progress report",
    ],
    notIncluded: [
      "Guaranteed rankings or traffic figures — no one can honestly promise these",
      "Paid advertising spend (see Digital Marketing)",
      "Link-buying or any practice that breaches search engine guidelines",
    ],
    timeframe: { value: "Monthly service; initial audit within one week", placeholder: false },
    pricing: "Request a Quote",
    pricingNote:
      "Pricing varies with the size of the website, the competitiveness of your market and the number of social profiles managed. A written quote is provided after a short discovery call.",
    image: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
    alt: "Laptop open on a search engine results page",
  },
  {
    slug: "digital-marketing",
    path: "/digital-marketing",
    numeral: "V",
    title: "Digital Marketing",
    short: "Google Ads campaign management and social media marketing for businesses.",
    description:
      "Planning and management of paid search campaigns on Google Ads, and organic social media marketing across the platforms your customers use — with content planning, publishing and monthly reporting.",
    included: [
      "Google Ads campaign setup and ongoing management",
      "Keyword, audience and budget planning",
      "Ad copy and landing-page recommendations",
      "Social media content calendar, copy and publishing",
      "Community replies and basic moderation",
      "Monthly plain-English performance report",
    ],
    notIncluded: [
      "Advertising spend paid to Google or social platforms (billed directly to you)",
      "Guaranteed results, leads or sales figures",
      "Influencer fees or paid placements",
    ],
    timeframe: { value: "Campaigns live within 2 weeks; monthly service", placeholder: false },
    pricing: "Request a Quote",
    pricingNote:
      "Our management fee depends on the number of platforms, campaign complexity and content volume. Advertising spend is separate and paid directly to the platforms.",
    image: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
    alt: "Marketing team planning a campaign on laptops and phones",
  },
];

export const WHY_US = [
  {
    title: "Written scope before work begins",
    text: "Every engagement starts with a written quotation that lists what is included, what is not, and when it will be delivered.",
  },
  {
    title: "Plain-English communication",
    text: "No jargon. You will always know what we are doing, why, and what it costs — and a real person answers the telephone.",
  },
  {
    title: "One partner for website and operations",
    text: "Website development and business management under one roof, so your online presence and day-to-day operations are planned together.",
  },
  {
    title: "No unverifiable promises",
    text: "We do not guarantee rankings, traffic or sales. We describe our work honestly and report on it monthly.",
  },
  {
    title: "Business-to-business only",
    text: "We work with companies and professional practices under written contracts. We do not offer consumer technical assistance of any kind.",
  },
];

export const PROCESS_STEPS = [
  {
    step: "01",
    title: "Enquiry",
    text: "You contact us by form, email or telephone and tell us what you need. We reply within the expected response time stated on our Contact page.",
  },
  {
    step: "02",
    title: "Discovery Call",
    text: "A short conversation to understand your business, goals, timeline and budget range, so we can scope the work accurately.",
  },
  {
    step: "03",
    title: "Written Quotation",
    text: "You receive a written quote describing the scope, deliverables, exclusions, delivery timeframe, payment terms and the applicable policies.",
  },
  {
    step: "04",
    title: "Agreement & Deposit",
    text: "Once you accept the quote in writing and any agreed deposit is paid, the project is scheduled and work begins.",
  },
  {
    step: "05",
    title: "Delivery & Revisions",
    text: "Work is delivered in agreed stages. You review each stage and request revisions within the scope set out in the quotation.",
  },
  {
    step: "06",
    title: "Handover & Support",
    text: "Final deliverables are handed over on receipt of the balance. Ongoing maintenance or monthly services continue as agreed.",
  },
];

export const FAQ_ITEMS = [
  {
    q: "What exactly does My Geeks LLC do?",
    a: "We are a business-to-business consultancy. We provide website development (design, development, redesign, maintenance, performance and basic SEO setup for business websites), custom software development (business applications, portals, dashboards and integrations) and business management services (process consulting, operations support, workflow planning, documentation and digital setup assistance). We also offer SEO/SMO and digital marketing for businesses that want to be found online.",
  },
  {
    q: "Do you offer remote IT support or computer repair?",
    a: "No. We provide exclusive B2B consulting and custom software development. We do not offer remote IT support, computer repair, or consumer technical assistance of any kind.",
  },
  {
    q: "How do I get a price?",
    a: "Use the Request a Quote form on our Contact page, email us, or telephone. After a short discovery call we send a written quotation covering scope, deliverables, exclusions, timeframe and payment terms. There is no charge for a quotation.",
  },
  {
    q: "Why is pricing not listed on the website?",
    a: "Because every website and every business is different. The cost of a website depends on the number of pages, design complexity, integrations and content readiness; management engagements depend on the scope agreed. Rather than print a figure that may not apply to you, we quote each project individually in writing.",
  },
  {
    q: "How do I pay?",
    a: "We issue invoices for the milestones in your quotation — for projects, 50% on acceptance and 50% on completion; for monthly services, in advance each month. Invoices are due within 3 days and can be paid by credit/debit card (secure payment link), bank transfer (ACH/wire) or PayPal. We do not take card payments on this website itself. See our Payment Information page.",
  },
  {
    q: "Can I cancel a project or monthly service?",
    a: "Yes. Monthly services can be cancelled by written notice and end at the close of the current billing month, with no extra notice period. Projects can be cancelled at any time; refunds follow our Standard Refund Framework — full if work has not started, proportional for active milestones, none for completed and approved phases. Full details are on our Refund & Cancellation Policy page.",
  },
  {
    q: "Do I own the website and content you produce?",
    a: "Once the final invoice is paid in full, the deliverables created specifically for you — design, code and content — are yours. Third-party components remain subject to their own licences. See our Terms & Conditions for details.",
  },
  {
    q: "Do you guarantee search rankings or sales?",
    a: "No. Rankings, traffic and sales depend on many factors outside anyone's control. We describe our work honestly, follow search engine guidelines, and report monthly on what was done and what changed.",
  },
  {
    q: "Where are you based and who do you work with?",
    a: `We operate from ${CONTACT.address}, and work remotely with small and mid-sized businesses. Projects are delivered online, with meetings by telephone or video call.`,
  },
];

export const SERVICE_OPTIONS = [
  "Website Development — New Website",
  "Website Development — Landing Page",
  "Website Development — Redesign",
  "Website Development — Maintenance / Performance",
  "Software Development — Custom Application / Portal",
  "Software Development — Integration / Automation",
  "Software Development — Maintenance Retainer",
  "Business Management — Process Consulting",
  "Business Management — Operations Support",
  "Business Management — Documentation / Digital Setup",
  "SEO / SMO Services",
  "Digital Marketing — Google Ads",
  "Digital Marketing — Social Media",
  "Not Sure Yet — Please Advise",
];

export const BUDGET_OPTIONS = [
  "Prefer not to say",
  "Under USD 1,000",
  "USD 1,000 – 2,500",
  "USD 2,500 – 5,000",
  "USD 5,000 – 10,000",
  "Over USD 10,000",
];
